import type { Scene } from "../types/scene";
import { useSelectionStore } from "../stores/selectionStore";

type PruneBodyRecord = Record<
  string,
  { uuid?: unknown; geoms?: Array<{ uuid?: unknown }>; inertia?: unknown } | undefined
>;

function resolveGeomInBodies(bodies: PruneBodyRecord, geomKey: string | null) {
  if (!geomKey) return null;
  const keyString = String(geomKey);
  const sep = keyString.indexOf(":");
  const hintedBodyUuid = sep > 0 ? keyString.slice(0, sep) : null;
  const geomUuid = sep > 0 ? keyString.slice(sep + 1) : keyString;
  if (!geomUuid) return null;

  if (hintedBodyUuid && bodies?.[hintedBodyUuid]?.geoms) {
    const hintedGeom = bodies[hintedBodyUuid].geoms?.find(
      (geom) => geom && String(geom.uuid) === geomUuid
    );
    if (hintedGeom) {
      return { sourceBodyUuid: hintedBodyUuid, geom: hintedGeom };
    }
  }

  for (const body of Object.values(bodies || {})) {
    if (!body?.uuid || !Array.isArray(body.geoms)) continue;
    const found = body.geoms.find((geom) => geom && String(geom.uuid) === geomUuid);
    if (found) {
      return { sourceBodyUuid: body.uuid, geom: found };
    }
  }
  return null;
}

/**
 * Scene restore and structural edits can delete entities without touching the selection store.
 * Clear primary selections that no longer resolve so inspector panels cannot act on stale IDs.
 */
export function pruneStaleSelectionForScene(scene: Partial<Scene>) {
  const sel = useSelectionStore.getState();
  const bodies = (scene.bodies || {}) as PruneBodyRecord;
  const joints = (scene.joints || {}) as Record<string, unknown>;
  const sites = (scene.sites || {}) as Record<string, unknown>;
  const meshes = (scene.meshes || {}) as Record<string, unknown>;
  const equalities = (scene.equality || {}) as Record<string, unknown>;
  const tendons = (scene.tendons || {}) as Record<string, unknown>;
  const cameras = (scene.cameras || {}) as Record<string, unknown>;
  const lights = (scene.lights || {}) as Record<string, unknown>;
  const actuators = (scene.actuators || {}) as Record<string, unknown>;

  if (sel.selectedGeom && !resolveGeomInBodies(bodies, sel.selectedGeom)) {
    sel.selectGeom(null);
  }
  if (sel.selectedJoint && !joints[sel.selectedJoint]) {
    sel.selectJoint(null);
  }
  if (sel.selectedSite && !sites[sel.selectedSite]) {
    sel.selectSite(null);
  }
  if (sel.selectedBody && !bodies[sel.selectedBody]) {
    sel.selectBody(null);
  }
  if (sel.selectedBodies.length > 0) {
    const next: string[] = [];
    for (const id of sel.selectedBodies) {
      if (bodies[id]) next.push(id);
    }
    if (next.length !== sel.selectedBodies.length) {
      sel.setSelectedBodies(next);
    }
  }
  if (sel.selectedMesh && !meshes[sel.selectedMesh]) {
    sel.selectMesh(null);
  }
  if (sel.selectedEquality && !equalities[sel.selectedEquality]) {
    sel.selectEquality(null);
  }
  if (sel.selectedTendon && !tendons[sel.selectedTendon]) {
    sel.selectTendon(null);
  }
  if (sel.selectedCamera && !cameras[sel.selectedCamera]) {
    sel.selectCamera(null);
  }
  if (sel.selectedLight && !lights[sel.selectedLight]) {
    sel.selectLight(null);
  }
  if (sel.selectedActuator && !actuators[sel.selectedActuator]) {
    sel.selectActuator(null);
  }
  if (sel.selectedInertial) {
    const str = String(sel.selectedInertial);
    const sepIdx = str.indexOf(":");
    const bodyUuid = sepIdx > 0 ? str.slice(0, sepIdx) : null;
    const body = bodyUuid ? bodies[bodyUuid] : null;
    if (!body?.inertia) {
      sel.selectInertial(null);
    }
  }
}
